import React, { useState, useEffect, useRef, useMemo } from 'react';
import { UserRole, isAdmin, isInstructor, isPremium } from '@shared/auth-simple';

import { Link } from 'wouter';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import {
  Menu,
  X,
  LogOut,
  Shield,
  Home,
  BookOpen,
  Library,
  Calendar,
  Crown,
  Settings,
  Users,
  Scale,
  HelpCircle,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { BlurPresets } from '@/utils/blurEffects';
import { NotificationBell } from '@/components/shared/NotificationBell';

interface MembershipSubscription {
  id: string;
  status: string;
  plan: {
    name: string;
    tier?: string;
  };
}

interface User {
  id: string;
  username?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  walletAddress?: string;
  level?: number;
  xp?: number;
  role?: string;
  roles?: string | string[];
  profileImage?: string;
}

interface PrivateNavProps {
  user: User;
}

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  glowColor: string;
  badge?: React.ReactNode;
  access:
    | 'all'
    | 'premium'
    | 'equity'
    | 'admin'
    | 'instructor'
    | 'premium_plus'
    | 'beta'
    | 'tier2'
    | 'tier3'
    | 'tier5';
  testId?: string;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const PrivateNav = React.memo(function PrivateNav({ user }: PrivateNavProps) {
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const userIsAdmin = isAdmin(user.role as UserRole);
  const userIsInstructor = isInstructor(user.role as UserRole);
  const userIsEquityStudent = user.role === UserRole.STUDENT;
  const hasPremiumAccess = user?.role ? isPremium(user.role as UserRole) : false;

  const { data: membershipData } = useQuery<{
    subscription: MembershipSubscription | null;
  }>({
    queryKey: ['/api/membership'],
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Lawful Notice Generator access — single source of truth shared with
  // LawfulDocsBetaGuard (same query key). Resolves to active paid membership.
  const { data: lawfulDocsAccessData } = useQuery<{ hasAccess: boolean }>({
    queryKey: ['/api/lawful-docs/access-check'],
    staleTime: 1000 * 60 * 5,
  });
  const hasLawfulDocsAccess =
    userIsAdmin || userIsInstructor || lawfulDocsAccessData?.hasAccess === true;

  // Derive Premium Plus access from membership subscription tier
  const hasPremiumPlusAccess =
    membershipData?.subscription?.plan?.tier === 'premium_plus' || userIsInstructor || userIsAdmin;

  const { data: curriculumAccess } = useQuery<{ ownedSlugs: string[] }>({
    queryKey: ['/api/curriculum/access'],
    staleTime: 1000 * 60 * 5,
    retry: 1,
    // PrivateNav only renders for authenticated users, but guard defensively
    enabled: !!user?.id,
  });

  // Map ownedSlugs → which tier indices are unlocked (0-based, matching CURRICULUM_STAGES).
  // Tier 2 = index 1, Tier 3 = index 2, Tier 4 = index 3, Tier 5 = index 4.
  const ownedTiers = useMemo<Set<number>>(() => {
    const slugs = new Set(curriculumAccess?.ownedSlugs ?? []);
    const owned = new Set<number>();
    const SLUGS: Record<number, string[]> = {
      1: ['foundational-lessons'],
      2: ['deep-current'],
      3: ['know-thy-self'],
      4: ['clear-the-debt', 'equity-discharge'],
      5: ['ministerium'],
      6: ['executor-intake'],
    };
    for (const [idx, tierSlugs] of Object.entries(SLUGS)) {
      if (tierSlugs.some(s => slugs.has(s))) owned.add(Number(idx));
    }
    return owned;
  }, [curriculumAccess]);

  const userName = user.displayName || user.firstName || user.username || 'Anonymous';
  const userRole = userIsAdmin ? 'Admin' : user.role || 'Member';
  const parts = userName.trim().split(/\s+/).filter(Boolean);
  const initials =
    parts.length === 0
      ? '?'
      : parts.length === 1
        ? parts[0].slice(0, 2)
        : `${parts[0][0]}${parts[parts.length - 1][0]}`;
  const userInitials = initials.toUpperCase();

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen && drawerRef.current) {
      const firstLink = drawerRef.current.querySelector('a, button') as HTMLElement;
      if (firstLink) {
        firstLink.focus();
      }
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobileMenuOpen]);

  // Check if a nav item is accessible to the current user.
  // Three derivation strategies: role-based (admin, instructor, premium, equity),
  // subscription-tier-based (premium_plus), and curriculum-tier-based (tier2/3/5).
  function canAccess(access: NavItem['access']): boolean {
    switch (access) {
      case 'all':
        return true;
      case 'premium':
        return hasPremiumAccess;
      case 'equity':
        return userIsEquityStudent || userIsInstructor || userIsAdmin;
      case 'admin':
        return userIsAdmin;
      case 'instructor':
        return userIsInstructor || userIsAdmin;
      case 'premium_plus':
        return hasPremiumPlusAccess;
      case 'beta':
        // Lawful Notice Generator — active paid membership required.
        return hasLawfulDocsAccess;
      case 'tier2':
        return hasPremiumAccess || userIsInstructor || userIsAdmin || ownedTiers.has(1);
      case 'tier3':
        return hasPremiumAccess || userIsInstructor || userIsAdmin || ownedTiers.has(2);
      case 'tier5':
        return (
          hasPremiumPlusAccess ||
          userIsEquityStudent ||
          userIsInstructor ||
          userIsAdmin ||
          ownedTiers.has(4)
        );
      default:
        return true;
    }
  }

  // Nav order follows frequency-of-use, not content taxonomy. Daily/weekly
  // destinations sit at the top (serial-position primacy); bursty one-time
  // tasks sink toward the bottom. Locked/upsell items are NOT shown here —
  // canAccess() hides them and the single "The Curriculum" link (top) leads
  // to the full tier ladder where every locked offering is presented.

  // BRITE nav — routed pages only (see App.tsx route table)
  const navSections: NavSection[] = [
    {
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          href: '/dashboard',
          icon: Home,
          glowColor: 'cyan',
          testId: 'button-dashboard',
          access: 'all',
        },
        {
          id: 'donate',
          label: 'Donate',
          href: '/donate',
          icon: Crown,
          glowColor: 'amber',
          access: 'all',
        },
      ],
    },
    {
      label: 'Learn',
      items: [
        {
          id: 'courses',
          label: 'Courses',
          href: '/courses',
          icon: BookOpen,
          glowColor: 'purple',
          access: 'all',
        },
        {
          id: 'library',
          label: 'Library',
          href: '/library',
          icon: Library,
          glowColor: 'pink',
          access: 'all',
        },
      ],
    },
    {
      label: 'Community',
      items: [
        {
          id: 'events',
          label: 'Events',
          href: '/calendar',
          icon: Calendar,
          glowColor: 'violet',
          access: 'all',
        },
      ],
    },
    {
      label: 'Manage',
      items: [
        {
          id: 'generator',
          label: 'Generator',
          href: '/generator',
          icon: Scale,
          glowColor: 'amber',
          access: 'instructor',
        },
        {
          id: 'pool',
          label: 'BRITE Pool',
          href: '/admin?section=pool',
          icon: Users,
          glowColor: 'green',
          access: 'instructor',
        },
      ],
    },
  ];

  const bottomItems: NavItem[] = [
    {
      id: 'account',
      label: 'Account',
      href: '/account',
      icon: Settings,
      glowColor: 'blue',
      testId: 'button-account',
      access: 'all',
    },
    {
      id: 'help',
      label: 'Help & Contact',
      href: '/help',
      icon: HelpCircle,
      glowColor: 'cyan',
      access: 'all',
    },
    {
      id: 'admin',
      label: 'Admin Dash',
      href: '/admin',
      icon: Shield,
      glowColor: 'amber',
      testId: 'button-admin',
      access: 'admin',
    },
  ];

  // Only accessible items render. Locked/upsell offerings are surfaced through
  // the single "The Curriculum" link, not shown here as locked rows.
  function renderNavItem(item: NavItem) {
    if (!canAccess(item.access)) return null;

    return (
      <Link key={item.id} href={item.href} onClick={() => setIsMobileMenuOpen(false)}>
        <div
          className="group w-full flex items-center gap-4 px-4 py-4 relative transition-all duration-500 overflow-hidden rounded-[1.3rem] text-white/70 hover:text-white hover:shadow-[0_0_20px_rgba(var(--glow),0.4),0_0_40px_rgba(var(--glow),0.2)] cursor-pointer"
          style={{ '--glow': glowRgb(item.glowColor) } as React.CSSProperties}
          data-testid={item.testId}
        >
          <div className="absolute inset-0 bg-glass-medium backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="absolute inset-0 rounded-[1.3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute inset-0 rounded-[1.3rem] bg-glass-medium backdrop-blur-md blur-sm"></div>
          </div>
          <div className="relative">
            <item.icon
              className="w-6 h-6 relative z-10 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-125 group-hover:rotate-12"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 rounded-full"
              style={{ backgroundColor: `rgba(${glowRgb(item.glowColor)}, 0.5)` }}
            />
          </div>
          <span className="font-medium text-lg tracking-wide relative z-10 flex-1">
            {item.label}
          </span>
          {item.badge}
        </div>
      </Link>
    );
  }

  return (
    <>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-cyber-purple text-white px-4 py-2 rounded-[1.3rem] focus:ring-2 focus:ring-cyber-cyan"
      >
        Skip to main content
      </a>

      {/* Main Navigation Header */}
      <nav
        className={`fixed top-[var(--nav-offset)] left-0 right-0 z-40 h-24 transition-[top] duration-300 ${BlurPresets.navbar}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <span className="font-serif text-3xl font-medium tracking-wide text-foreground">
                BRITE<span className="text-brite-terra">.</span>
              </span>
            </Link>

            {/* Right section with notification bell, username and hamburger menu */}
            <div className="flex items-center space-x-4">
              <NotificationBell isAuthenticated={true} />

              <span
                className="text-brite-terra font-medium text-sm md:text-base"
                aria-label={`Logged in as ${userName}`}
              >
                {userName}
              </span>

              <UnifiedButton
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-foreground hover:text-brite-terra p-2"
                data-testid="button-mobile-menu"
                aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="right-drawer"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" aria-hidden="true" />
                ) : (
                  <Menu className="w-6 h-6" aria-hidden="true" />
                )}
              </UnifiedButton>
            </div>
          </div>
        </div>
      </nav>

      {/* Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-md z-30"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Right Drawer */}
      <div
        ref={drawerRef}
        id="right-drawer"
        className={`fixed top-0 right-0 h-screen w-80 z-50 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5), inset 1px 0 1px rgba(255, 255, 255, 0.1)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="h-full overflow-y-auto">
          {/* User Section */}
          <Link href="/account" onClick={() => setIsMobileMenuOpen(false)}>
            <div
              className="p-6 border-b border-white/10 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors"
              style={{
                background:
                  'linear-gradient(135deg, rgba(0, 200, 255, 0.15) 0%, rgba(138, 43, 226, 0.15) 100%)',
              }}
            >
              <Avatar
                className="w-16 h-16 ring-2 ring-transparent hover:ring-cyber-cyan/50 transition-all"
                style={{
                  background: 'rgba(0, 200, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 200, 255, 0.3)',
                  boxShadow: '0 0 20px rgba(0, 200, 255, 0.2)',
                }}
              >
                <AvatarImage src={user.profileImage ?? undefined} alt="" loading="lazy" />
                <AvatarFallback className="bg-cyber-cyan/20 text-white text-lg">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-transparent bg-clip-text bg-glass-medium backdrop-blur-md font-semibold text-lg">
                  {userName}
                </h3>
                <p className="text-cyan-300/80 text-sm">{userRole}</p>
              </div>
            </div>
          </Link>

          {/* Navigation Sections */}
          <div className="p-4 space-y-1">
            {navSections.map((section, sectionIdx) => {
              const visibleItems = section.items.filter(item => canAccess(item.access));
              if (visibleItems.length === 0) return null;

              return (
                <div key={section.label || sectionIdx}>
                  {section.label && (
                    <div className="px-4 pt-4 pb-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-white/30">
                        {section.label}
                      </span>
                    </div>
                  )}
                  {visibleItems.map(item => renderNavItem(item))}
                </div>
              );
            })}

            {/* Divider */}
            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center">
                <div className="w-2 h-2 rounded-full bg-glass-medium backdrop-blur-md opacity-50"></div>
              </div>
            </div>

            {/* Bottom Items (Account, Help, Admin, Instructor) */}
            {bottomItems.map(item => renderNavItem(item))}

            {/* Divider */}
            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center">
                <div className="w-2 h-2 rounded-full bg-glass-medium backdrop-blur-md opacity-50"></div>
              </div>
            </div>

            {/* Sign Out */}
            <UnifiedButton
              onClick={handleLogout}
              className="group w-full flex items-center gap-4 px-4 py-4 relative text-white/70 hover:text-white transition-all duration-500 overflow-hidden rounded-[1.3rem] hover:shadow-[0_0_20px_rgba(239,68,68,0.4),0_0_40px_rgba(239,68,68,0.2)] cursor-pointer"
              data-testid="button-sign-out"
            >
              <div className="absolute inset-0 bg-glass-medium backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 rounded-[1.3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 rounded-[1.3rem] bg-glass-medium backdrop-blur-md blur-sm"></div>
              </div>
              <div className="relative">
                <LogOut
                  className="w-6 h-6 relative z-10 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-125 group-hover:rotate-12"
                  aria-hidden="true"
                />
                <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/50 blur-xl transition-all duration-500 rounded-full" />
              </div>
              <span className="font-medium text-lg tracking-wide relative z-10">Sign Out</span>
            </UnifiedButton>
          </div>
        </div>
      </div>
    </>
  );
});

function glowRgb(color: string): string {
  const map: Record<string, string> = {
    cyan: '0,200,255',
    blue: '59,130,246',
    purple: '168,85,247',
    pink: '236,72,153',
    green: '34,197,94',
    amber: '251,191,36',
    yellow: '245,158,11',
    violet: '139,92,246',
    red: '239,68,68',
  };
  return map[color] || '0,200,255';
}

export default PrivateNav;
